import { skipToken } from '@reduxjs/toolkit/query';
import { useCreateDialogChip, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import Loader from 'components/Loader';
import useAuth from 'hooks/useAuth.ts';
import { useCallback, useEffect, useState } from 'react';
import { useGetActiveBankAccountsQuery } from 'store/api/companies';
import { useGetContactByIdQuery } from 'store/api/contacts';
import { useGetEmployeeByIdQuery } from 'store/api/employees';
import { ContactForm, PartyType, RelationshipTypeCode } from 'types/contacts';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { SegmentType } from 'types/segment.ts';
import { ServiceLevelType } from 'types/service-level.ts';
import { getOptionsFromClassifier } from 'utils/formUtil.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { blocksToForm } from './blocking/BlockingPolicyReadonlyShared';
import { CONTACT_FORM_CLASSIFIERS, contactDtoToForm } from './contactFormMapping';
import ContactPageForm from './ContactPageForm';
import { Setting } from 'types/back-office-settings';
import { useSettings } from 'utils/useSettings';

interface ContactPrefill {
    partyType?: PartyType;
    relationshipType?: RelationshipTypeCode;
    employedBy?: Array<{ id: number; name: string }>;
}

const ContactPageAdd = () => {
    const [initialData, setInitialData] = useState<ContactForm>();
    const { user } = useAuth();
    const userId = user?.id;
    // In the create dialog the router describes the page behind it; the opener's state is on the route.
    const { location } = useCreateRoute();
    const prefill =
        location.state && typeof location.state === 'object' && 'prefill' in location.state
            ? (location.state as { prefill: ContactPrefill }).prefill
            : undefined;
    useCreateDialogChip({ parent: prefill?.employedBy?.[0]?.name });

    const isContactDefaultStatusClient = useSettings().isSettingEnabled(Setting.NEW_CONTACTS_DEFAULT_TO_CLIENT);

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: CONTACT_FORM_CLASSIFIERS,
    });

    const { initialCustomFieldsObject: initialPersonCustomFieldsObject, isLoading: isLoadingPersonCustomFieldData } =
        useCustomFields({
            entityType: EntityType.PARTY_PERSON,
        });

    const { initialCustomFieldsObject: initialCompanyCustomFieldsObject, isLoading: isLoadingCompanyCustomFieldData } =
        useCustomFields({
            entityType: EntityType.PARTY_COMPANY,
        });

    const { data: currentEmployeeData, isLoading: isLoadingCurrentEmployee } = useGetEmployeeByIdQuery(
        userId ? userId.toString() : skipToken
    );

    // Set initial form data
    const getInitialData = useCallback((): ContactForm | undefined => {
        if (classifiers) {
            const defaultServiceLevelType =
                getOptionsFromClassifier<string, string>(classifiers['SERVICE_LEVEL_TYPE']).find(
                    (val) => val.value === ServiceLevelType.EXPLORE
                ) ?? null;

            const defaultSegmentType =
                getOptionsFromClassifier<string, string>(classifiers['SEGMENT_TYPE']).find(
                    (val) => val.value === SegmentType.NEW
                ) ?? null;

            return {
                partyType: prefill?.partyType ?? PartyType.COMPANY,
                relationshipType:
                    prefill?.relationshipType ??
                    (isContactDefaultStatusClient ? RelationshipTypeCode.CLIENT : RelationshipTypeCode.NON_CLIENT),
                name: '',
                officialLocalName: '',
                position: '',
                mainEmail: '',
                mainPhone: '',
                mainMobile: '',
                statusCode: 'ACTIVE',
                clientReference: '',
                preferredLanguage: null,
                billingNotes: '',
                invoicingInfo: '',
                debtNotes: '',
                debtExplanation: '',
                debtHistory: '',
                notes: '',
                coiNotes: '',
                businessAreas: [],
                relatedKeywords: [],
                managers: [],
                partners: [],
                administrators: [],
                businessContacts: currentEmployeeData
                    ? [
                          {
                              id: currentEmployeeData.id,
                              name: `${currentEmployeeData.firstName} ${currentEmployeeData.lastName}`,
                          },
                      ]
                    : [],
                paymentNotificationReceivers: [],
                billingAddress: null,
                billingLocalAddress: null,
                personalCode: '',
                personTitle: null,
                personManualTitle: '',
                linkedinUrl: '',
                homeAddress: null,
                homeLocalAddress: null,
                firstName: '',
                lastName: '',
                localFirstName: '',
                localLastName: '',
                officialName: '',
                regCode: '',
                notVatLiable: false,
                vatRegNum: '',
                vatCountryCode: null,
                website: '',
                einvoiceAddress: '',
                einvoiceOperatorCode: '',
                einvoiceOperatorType: null,
                employees: [],
                employedBy: prefill?.employedBy ?? [],
                primaryContact: null,
                primaryContactForCompanies: [],
                partyGroups: [],
                registrationAddress: null,
                registrationLocalAddress: null,
                visitingAddress: null,
                correspondenceAddress: null,
                segmentType: defaultSegmentType,
                serviceLevelType: defaultServiceLevelType,
                payerParty: null,
                useJointInvoicing: false,
                issueInvoicesWithCurrentDate: false,
                isInternalClient: false,
                primaryRecipientParty: null,
                primaryRecipientAdditionalEmails: '',
                secondaryRecipientParties: [],
                secondaryRecipientAdditionalEmails: '',
                invoiceArchiveEmail: '',
                invoiceTemplate: null,
                invoiceSpecTemplate: null,
                isSpecIncludedInInvoice: null,
                invoiceCustomization: null,
                dmsLink: '',
                isPoliticallyExposedPerson: false,
                identityDocumentNumber: null,
                identityDocumentDateOfIssue: null,
                identityDocumentDateOfExpiry: null,
                identityDocumentIssuingCountryCode: null,
                identityDocumentIssuingAuthority: null,
                invoicingDeadlineDay: null,
                invoicingPaymentDays: null,
                preferredBankAccount: null,
                externalBillingInfo: '',
                isPersonaNonGrata: false,
                riskProfileTypeCode: null,
                riskProfileNotes: '',
                complianceVerificationStatusTypeCode: null,
                complianceVerificationNotes: '',
                preferredMailingLanguageTypeCode: null,
                countries: [],
                mailingPreferences: [],
                marketingAreaTypes: [],
                areaOfResponsibilityTypes: [],
                cooperationAreaTypes: [],
                isServiceAgreementNotNeeded: false,
                isUboNotNeeded: false,
                changeReasons: null,
                blocking: blocksToForm(null),
                ...initialPersonCustomFieldsObject,
                ...initialCompanyCustomFieldsObject,
            };
        }

        return undefined;
    }, [
        classifiers,
        initialPersonCustomFieldsObject,
        initialCompanyCustomFieldsObject,
        currentEmployeeData,
        prefill,
        isContactDefaultStatusClient,
    ]);

    useEffect(() => {
        if (isLoadingPersonCustomFieldData || isLoadingCompanyCustomFieldData || isLoadingCurrentEmployee) {
            return;
        }
        setInitialData(getInitialData());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classifiers, isLoadingPersonCustomFieldData, isLoadingCompanyCustomFieldData, isLoadingCurrentEmployee]);

    if (isLoadingClassifiers || isLoadingCurrentEmployee || initialData === undefined) {
        return <Loader />;
    }

    return <ContactPageForm type="add" initialData={initialData} classifiers={classifiers} />;
};

/** The add form loaded with what a draft holds, read as a contact's edit page reads it. */
const ContactDraftAdd = ({ draftId }: { draftId: number }) => {
    const [initialData, setInitialData] = useState<ContactForm>();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: CONTACT_FORM_CLASSIFIERS });
    const { data: contact } = useGetContactByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { data: bankAccountsGrouped = [], isLoading: isLoadingBankAccounts } = useGetActiveBankAccountsQuery();
    const { initialCustomFieldsObject, isLoading: isLoadingCustomFields } = useCustomFields({
        entityType: contact
            ? contact.partyType === PartyType.COMPANY
                ? EntityType.PARTY_COMPANY
                : EntityType.PARTY_PERSON
            : null,
        fields: contact?.customFields as CustomField[] | undefined,
    });

    // Frozen once loaded, as the edit page's: a refetch must not reset what the user is typing.
    if (!initialData && contact && !isLoadingBankAccounts && !isLoadingClassifiers && !isLoadingCustomFields) {
        setInitialData(
            contactDtoToForm(contact, {
                classifiers,
                bankAccountsGrouped,
                customFieldValues: initialCustomFieldsObject,
            })
        );
    }

    return initialData ? (
        <ContactPageForm type="add" initialData={initialData} classifiers={classifiers} />
    ) : (
        <Loader />
    );
};

/** The contact create; its URL with `?draft=<id>` edits that draft instead of a blank contact. */
const ContactPageAddOrDraft = () => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <ContactPageAdd /> : <ContactDraftAdd key={draftId} draftId={draftId} />;
};

export default ContactPageAddOrDraft;
